import { Loading } from '@affine/component';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import * as styles from './avatar-upload-overlay.css';

const AVATAR_UPLOAD_OVERLAY_FADE_MS = 180;

function toCssRadius(radius: number | string | undefined) {
  return typeof radius === 'number' ? `${radius}px` : (radius ?? '50%');
}

export function useAvatarUploadTask() {
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const runningRef = useRef(false);

  const runAvatarUploadTask = useCallback(async (task: () => Promise<void>) => {
    if (runningRef.current) {
      return;
    }

    runningRef.current = true;
    setIsAvatarUploading(true);
    try {
      await task();
    } finally {
      runningRef.current = false;
      setIsAvatarUploading(false);
    }
  }, []);

  return { isAvatarUploading, runAvatarUploadTask };
}

export const AvatarUploadOverlay = ({
  uploading,
  rounded,
  children,
}: {
  uploading: boolean;
  rounded?: number | string;
  children: ReactNode;
}) => {
  const [shouldRenderOverlay, setShouldRenderOverlay] = useState(uploading);

  useEffect(() => {
    if (uploading) {
      setShouldRenderOverlay(true);
      return;
    }

    const timeout = setTimeout(() => {
      setShouldRenderOverlay(false);
    }, AVATAR_UPLOAD_OVERLAY_FADE_MS);

    return () => {
      clearTimeout(timeout);
    };
  }, [uploading]);

  return (
    <span className={styles.avatarUploadFrame} aria-busy={uploading}>
      {children}
      {shouldRenderOverlay ? (
        <span
          className={styles.avatarUploadOverlay}
          data-visible={uploading}
          style={{ borderRadius: toCssRadius(rounded) }}
          role="progressbar"
          aria-label="Uploading avatar"
        >
          <Loading
            className={styles.avatarUploadSpinner}
            size={22}
            strokeWidth={3}
            strokeColor="white"
          />
        </span>
      ) : null}
    </span>
  );
};
